import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { fabVisible } from "@/components/hara/PlateTray";

export const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
let lastPress = { x: 50, y: 50 };

// Text set on a circular path; optional slow CSS rotation (paused off-screen via .avant-spin visibility rules).
export const RingText = ({ r, text, size = 28, spacing = "0.32em", dur = 120, reverse, fill = "#0B2E44", weight = 500, id }) => (
  <g className="avant-spin" style={{ animationDuration: `${dur}s`, animationDirection: reverse ? "reverse" : "normal" }}>
    <defs><path id={id} d={`M500 ${500 - r} a ${r} ${r} 0 1 1 -0.01 0`} /></defs>
    <text fill={fill} fontSize={size} fontFamily="Switzer, sans-serif" fontWeight={weight} letterSpacing={spacing}>
      <textPath href={`#${id}`}>{text}</textPath>
    </text>
  </g>
);

// Hairline that draws itself when scrolled into view.
export const Hairline = ({ className = "", color = "#0B2E44", opacity = 0.25, delay = 0 }) => (
  <motion.span aria-hidden className={`block h-px origin-left ${className}`} style={{ background: color, opacity }}
    initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, delay, ease: [0.65, 0, 0.35, 1] }} />
);

// Section numeral + wide-tracked label on a drawing hairline (Alchemist-style).
export const Numeral = ({ n, label, testId }) => (
  <div data-testid={testId} className="flex items-center gap-4">
    <span className="font-ui text-[15px] font-normal tracking-[0.3em] text-[#B8326C]">{String(n).padStart(2, "0")}</span>
    {label && <span className="font-ui whitespace-nowrap text-[15px] font-normal uppercase tracking-[0.32em] text-[#0B2E44]">{label}</span>}
    <Hairline className="flex-1" />
  </div>
);

// Letter-by-letter reveal; parts = [{ t, cls }].
export const Kinetic = ({ parts, delay = 0.3, step = 0.035, className = "", label }) => {
  let k = 0;
  return (
    <span aria-label={label} className={className}>
      {parts.map((p, i) => (
        <span key={i} aria-hidden className={`inline-block whitespace-nowrap ${p.cls || ""}`}>
          {[...p.t].map((ch, j) => {
            k += 1;
            return (
              <span key={j} className="inline-block overflow-hidden pb-[0.1em] align-bottom">
                <motion.span className="inline-block" initial={{ y: "115%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }}
                  transition={{ delay: delay + k * step, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>{ch === " " ? "\u00A0" : ch}</motion.span>
              </span>
            );
          })}
        </span>
      ))}
    </span>
  );
};

// Bold sculptural overlapping circles/arcs drifting behind every guest page, with gentle scroll parallax.
const SHAPES = [
  { c: "#CFE9E2", o: 0.45, s: 64, x: -18, y: 22, f: 0.06, d: 60 },
  { c: "#A8D8D7", o: 0.3, s: 46, x: 66, y: -14, f: 0.1, d: 72 },
];
export const SculptBG = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (reducedMotion()) return undefined;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => ref.current?.querySelectorAll("[data-f]").forEach((el) => { el.style.translate = `0 ${-window.scrollY * +el.dataset.f}px`; }));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);
  return (
    <div ref={ref} aria-hidden data-testid="sculpt-bg" className="pointer-events-none fixed inset-0 z-[1] overflow-hidden">
      {SHAPES.map((s, i) => (
        <span key={i} data-f={s.f} className="absolute block">
          <span className="sculpt-drift block rounded-full" style={{ width: `${s.s}vmax`, height: `${s.s}vmax`, left: `${s.x}vw`, top: `${s.y}vh`, position: "fixed", background: s.c, opacity: s.o, animationDuration: `${s.d}s`, animationDelay: `${-i * 7}s` }} />
        </span>
      ))}
    </div>
  );
};

// Ring ripple on every press; also remembers the press point for the circle-wipe.
export const PressRipples = () => {
  useEffect(() => {
    const on = (e) => {
      lastPress = { x: (e.clientX / window.innerWidth) * 100, y: (e.clientY / window.innerHeight) * 100 };
      if (reducedMotion() || !e.target.closest?.("button, a, [role=button]")) return;
      const el = document.createElement("span");
      el.className = "press-ring";
      el.style.left = `${e.clientX}px`;
      el.style.top = `${e.clientY}px`;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 800);
    };
    document.addEventListener("pointerdown", on, { passive: true });
    return () => document.removeEventListener("pointerdown", on);
  }, []);
  return null;
};

// Circle-wipe page transition from the last press point.
export const PageWipe = ({ children }) => {
  const { pathname } = useLocation();
  const [done, setDone] = useState(false);
  const first = useRef(true);
  useEffect(() => { setDone(false); }, [pathname]);
  const skip = first.current || reducedMotion();
  first.current = false;
  const at = `${lastPress.x}% ${lastPress.y}%`;
  return (
    <motion.div key={pathname} data-testid="page-wipe" initial={skip ? false : { clipPath: `circle(0% at ${at})` }} animate={{ clipPath: `circle(150% at ${at})` }}
      transition={{ duration: 0.95, ease: [0.65, 0, 0.35, 1] }} onAnimationComplete={() => setDone(true)} style={done || skip ? { clipPath: "none" } : undefined} className={`relative z-[2] ${fabVisible(pathname) ? "pb-[84px]" : ""}`}>
      {children}
    </motion.div>
  );
};
